/// <reference path="../definitions/QueryTs.d.ts" />
/// <reference path="../definitions/Site.d.ts" />

// Make navigator show Share
interface Window {
    zESettings: any;
    zE: any;
}

type UnauthenticatedResponseTypes = 'Fido' | 'TOTP' | 'Email';

interface IUnauthenticatedTwoFactor {
    type: UnauthenticatedResponseTypes;
}

interface IFidoUnauthenticatedTwoFactor
    extends IUnauthenticatedTwoFactor {
    type: UnauthenticatedResponseTypes;
    uniqueIdentifier: string;
}

interface IUnauthenticatedResponse {
    type: string;
    data: IUnauthenticatedTwoFactor | string;
}

class Startup
    extends QueryElement<HTMLElement> {

    private readonly _startup: IQueryElement;
    private readonly _error: IQueryElement;

    private readonly _password: IQueryElement;
    private readonly _username: IQueryElement;

    constructor(element: HTMLElement) {
        super(element);
        let startup = this.query(".startup").first();
        let error = this.query(".error").first();

        let username = this.query(".login-credentials input[name=username]").first();
        let password = this.query(".login-credentials input[name=password]").first();
        let loginButton = this.query(".login-credentials button").first();

        if (username === null || password === null || loginButton === null || startup === null || error === null)
            throw "An element couldn't be found";

        this._startup = startup;
        this._error = error;

        this._username = username;
        this._password = password;

        // Add events
        this._username.on("keydown", async (e: KeyboardEvent) => {
            if (e.keyCode == 13)
                this._password.focus();
        });
        this._password.on("keydown", async (e: KeyboardEvent) => {
            if (e.keyCode == 13)
                await this.login();
        })
        loginButton.on("click", async () => { await this.login(); });

        this.getConfig();
    }
    async getConfig() {
        let request = new QueryRequest("/Home/Config/");

        try {
            let headers = await request.request();

            if (headers.status == 200) {
                let response = await headers.json();

                // Load all stylesheets
                let elements = document.querySelectorAll("link[media=async]");
                for (let x = 0; x < elements.length; x++) {
                    (<HTMLStyleElement>elements[x]).media = 'all';
                }

                let element = document.getElementById("content");

                if (element == null)
                    throw "Cannot find content element";

                // Start site
                var config = QueryJSON.cast(SiteConfig, response.site);
                let site = new Site(
                    element,
                    config,
                    response.menu as IMenuConfig
                );

                // Show overview
                if (config.startPage == null) {
                    site.overview(false);
                } else {
                    let container = await site.createContainer({
                        title: config.startPageName,
                        color: "#802175"
                    });
                    await container.start({
                        actionLink: {
                            actionInfo: config.startPage
                        },
                        type: "Url"
                    });
                }

                this.remove();
            } else {
                let response = await headers.text();
                this.showLogin(response);
            }
        } catch (e) {
            await delay(5000);
            this.getConfig();
        }
    }
    async showFido(uniqueIdentifier: string) {
        this.showMessage("<h1>Attempting FIDO authentication...</h1>");

        // -- Request Fido
        let fidoRequest = await new QueryRequest('Api/FidoLoginRequest', {
            contentType: 'json',
            method: 'post',
            data: JSON.stringify({
                uniqueIdentifier: uniqueIdentifier
            })
        }).request();

        let fidoResponse = await fidoRequest.json();

        // Convert some properties
        fidoResponse.challenge = Fido.coerceToArrayBuffer(fidoResponse.challenge);
        fidoResponse.allowCredentials.forEach(function (listItem: any) {
            listItem.id = Fido.coerceToArrayBuffer(listItem.id);
        });

        let newCredential: PublicKeyCredential;
        let newCredentialResponse: AuthenticatorAssertionResponse;
        try {
            newCredential = (await navigator.credentials.get({
                publicKey: fidoResponse
            }) as PublicKeyCredential) ?? throwExpression("No credentials fetched.");

            newCredentialResponse = newCredential.response as AuthenticatorAssertionResponse
                ?? throwExpression("Wrong response.");
        } catch (e) {
            console.error(e);

            this.showMessage("<h1>Unable to authenticate</h1><h2>You will be redirected to the login page in 5 seconds...</h2>");
            await delay(5000);
            location.reload();

            return;
        }

        // -- Register to server
        let registerData = {
            id: newCredential.id,
            rawId: Fido.coerceToBase64Url(new Uint8Array(newCredential.rawId)),
            type: newCredential.type,
            extensions: newCredential.getClientExtensionResults(),
            response: {
                authenticatorData: Fido.coerceToBase64Url(new Uint8Array(newCredentialResponse.authenticatorData)),
                clientDataJson: Fido.coerceToBase64Url(new Uint8Array(newCredentialResponse.clientDataJSON)),
                signature: Fido.coerceToBase64Url(new Uint8Array(newCredentialResponse.signature))
            }
        };

        let registerRequest = await new QueryRequest('Api/FidoLogin', {
            contentType: 'json',
            method: 'post',
            data: JSON.stringify({
                uniqueIdentifier: uniqueIdentifier,
                response: registerData
            })
        }).request();

        switch (registerRequest.status) {
            default:
            case 200:
                await this.getConfig();
                break;
            case 401:
                let response = await registerRequest.text();
                await this.showLogin(response);
                break;
        }
    }
    setError(error:string) {
        if (error != "")
            this._error.addClass("show");
        else
            this._error.removeClass("show");

        this._error.setHtml(error);
    }
    showLogin(error: string) {
        this.setError(error);
        this.removeClass("startup")
            .addClass("login");
    }
    resetLogin() {
        this._error.removeClass("show");
        this.query(".login-credentials").first()?.removeClass("hide");
        this.query(".login-google-authenticator").first()?.addClass("hide");
        this.query(".login-email").first()?.addClass("hide");
    }
    showTOTP(error: string) {
        this.setError(error);

        this.query(".login-credentials").first()?.addClass("hide");
        const field = this.query(".login-google-authenticator").first();
        if (field != null)
        {
            field.removeClass("hide");
            if (field.containsClass("initialized"))
                return;
            
            field.addClass("initialized");

            const input = field.query("input").first();
            const cancel = field.query("button.cancel").first();
            const login = field.query("button.login").first();

            if (input == null || cancel == null || login == null)
                throw "Element(s) not found";
            
            input.on("keydown", (a: KeyboardEvent) => a.keyCode == 13 ? login.trigger("click", false) : undefined);
            cancel.on("click", () => this.resetLogin());
            login.on("click", () => this.submitTOTP(input.getVal() ?? ""));
        }
    }
    async submitTOTP(code: string) {
        this.showMessage("<h1>Attempting TOTP authentication...</h1>");
        let registerRequest = await new QueryRequest('Api/TOTPAuthenticationRequest', {
            contentType: 'json',
            method: 'post',
            data: JSON.stringify({
                code: code
            })
        }).request();

        switch (registerRequest.status) {
            default:
            case 200:
                await this.getConfig();
                break;
            case 401:
                let response = await registerRequest.text();
                this.resetLogin();
                this.showLogin("");
                await this.showTOTP(response);
                break;
        }
    }
    showEmail(error: string) {
        this.setError(error);

        this.query(".login-credentials").first()?.addClass("hide");
        var field = this.query(".login-email").first();
        if (field != null)
        {
            field.removeClass("hide");
            if (field.containsClass("initialized"))
                return;
            
            field.addClass("initialized");

            const input = field.query("input").first();
            const cancel = field.query("button.cancel").first();
            const login = field.query("button.login").first();

            if (input == null || cancel == null || login == null)
                throw "Element(s) not found";
            
            input.on("keydown", (a: KeyboardEvent) => a.keyCode == 13 ? login.trigger("click", false) : undefined);
            cancel.on("click", () => this.resetLogin());
            login.on("click", () => this.submitEmail(input.getVal() ?? ""));
        }
    }
    async submitEmail(code: string) {
        this.showMessage("<h1>Attempting Email authentication...</h1>");
        let registerRequest = await new QueryRequest('Api/EmailAuthenticationRequest', {
            contentType: 'json',
            method: 'post',
            data: JSON.stringify({
                code: code
            })
        }).request();

        switch (registerRequest.status) {
            default:
            case 200:
                await this.getConfig();
                break;
            case 401:
                let response = await registerRequest.text();
                this.resetLogin();
                this.showLogin("");
                await this.showEmail(response);
                break;
        }
    }
    showMessage(message: string) {
        this._startup.setHtml(message);
        this.removeClass("login")
            .addClass("startup");
    }
    // TODO: Make it fully translatable
    async login() {
        // Show message
        this.showMessage("<h1>Attempting to log in...</h1>");

        // Try to login
        let request = new QueryRequest("/Api/Login/", {
            data: QueryJSON.serialize({
                "username": this._username.getVal(),
                "password": this._password.getVal()
            }),
            method: "post",
            contentType: "json"
        });
        try {
            let headers = await request.request();

            switch (headers.status) {
                case 200:
                    await this.getConfig();
                    break;
                case 401:
                    let responseData: IUnauthenticatedResponse;
                    try {
                        responseData = await headers.json();
                    } catch (error) {
                        responseData = {
                            type: "Error",
                            data: "We encountered an issue while processing your request",
                        };
                    }

                    if (responseData.type === "TwoFactorAuthentication") {
                        const data = responseData.data as IUnauthenticatedTwoFactor
                        switch(data.type)
                        {
                            case "Fido":
                                const dataFido = responseData.data as IFidoUnauthenticatedTwoFactor;
                                this.showFido(dataFido.uniqueIdentifier);
                                break;
                            case "Email":
                                this.showLogin("");
                                this.showEmail("");
                                break;
                            case "TOTP":
                                this.showLogin("");
                                this.showTOTP("");
                                break;
                        }
                    } else if (responseData.type === "Error") {
                        const errorMessage = responseData.data as string || "An error occurred.";
                        this.showLogin(errorMessage);
                    } else {
                        this.showLogin("Wrong username or password.");
                    }

                    break;
                default:
                case 500:
                    this.showMessage("<h1>Something went wrong.</h1><h2>Please contact us.</h2>");
                    break;
            }
        } catch (e) {
            // If something went wrong, just await config
            await this.getConfig();
        }
    }
}